import { useCallback, useEffect } from 'react'

import type { DateType } from '@/utils/date'
import type { DateRange } from 'react-day-picker'
import type { BranchType, FilterState } from './filterSlice'
import {
  initializeFilters,
  resetFilters,
  selectChartType,
  selectComparisonBranch,
  selectDate,
  selectDateFilterType,
  selectFilterState,
  selectPageRefreshTrigger,
  selectRefreshCallbacks,
  selectSelectedBranch,
  selectSelectedBranchType,
  selectSizeFilter,
  selectTemperatureFilter,
  selectTypeFilter,
  setChartType,
  setComparisonBranch,
  setDate,
  setDateFilterType,
  setSelectedBranch,
  setSelectedBranchType,
  setSizeFilter,
  setTemperatureFilter,
  setTypeFilter,
  triggerRefresh,
} from './filterSlice'
import { useAppDispatch, useAppSelector } from './hooks'

export const useInitializeFilters = (
  defaults: Partial<FilterState['defaults']> & { pageId?: string }
) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (
      defaults.defaultBranch &&
      defaults.defaultDate &&
      defaults.defaultBranchType
    ) {
      dispatch(
        initializeFilters({
          defaultBranch: defaults.defaultBranch,
          defaultBranchType: defaults.defaultBranchType,
          defaultComparisonBranch: defaults.defaultComparisonBranch,
          defaultDate: defaults.defaultDate,
          defaultDateFilterType:
            defaults.defaultDateFilterType || 'month-to-date',
          defaultTemperatureFilter: defaults.defaultTemperatureFilter,
          defaultSizeFilter: defaults.defaultSizeFilter,
          defaultTypeFilter: defaults.defaultTypeFilter,
          defaultChartType: defaults.defaultChartType,
        })
      )
    }
  }, [defaults.pageId, dispatch])
}

/**
 * BASE FILTER HOOK
 *
 * For regular pages (Sales, Labor, Speed of Service, Loyalty, Spend, PnL)
 * Provides core filter functionality without comparison or special filters
 */
export const useReduxBaseFilter = () => {
  const dispatch = useAppDispatch()

  // Select only the core filter state
  const selectedBranch = useAppSelector(selectSelectedBranch)
  const selectedBranchType = useAppSelector(selectSelectedBranchType)
  const date = useAppSelector(selectDate)
  const dateFilterType = useAppSelector(selectDateFilterType)
  const refreshTrigger = useAppSelector(selectRefreshCallbacks)

  // Action dispatchers wrapped in useCallback for stable references
  const handleSetSelectedBranch = useCallback(
    (branch: string, branchType: BranchType) => {
      dispatch(setSelectedBranch({ branch, branchType }))
    },
    [dispatch]
  )

  const handleSetSelectedBranchType = useCallback(
    (branchType: BranchType) => {
      dispatch(setSelectedBranchType(branchType))
    },
    [dispatch]
  )

  const handleSetDate = useCallback(
    (newDate: DateRange | undefined) => {
      dispatch(setDate(newDate))
    },
    [dispatch]
  )

  const handleSetDateFilterType = useCallback(
    (filterType: DateType) => {
      dispatch(setDateFilterType(filterType))
    },
    [dispatch]
  )

  const handleResetFilters = useCallback(() => {
    dispatch(resetFilters())
  }, [dispatch])

  const handleRefreshData = useCallback(
    (pageId: string) => {
      dispatch(triggerRefresh(pageId))
    },
    [dispatch]
  )

  return {
    selectedBranch,
    selectedBranchType,
    setSelectedBranch: handleSetSelectedBranch,
    setSelectedBranchType: handleSetSelectedBranchType,
    date,
    dateFilterType,
    setDate: handleSetDate,
    setDateFilterType: handleSetDateFilterType,
    resetFilters: handleResetFilters,
    refreshData: handleRefreshData,
    refreshTrigger, // Components can watch this to know when to refetch
  }
}

/**
 * COMPARISON FILTER HOOK
 *
 * For lens pages that need comparison branches (PnL Lens)
 * Includes all base filters plus comparison branch
 */
export const useReduxComparisonFilter = () => {
  const baseFilters = useReduxBaseFilter()
  const dispatch = useAppDispatch()

  // Select comparison-specific state
  const comparisonBranch = useAppSelector(selectComparisonBranch) || ''

  const handleSetComparisonBranch = useCallback(
    (branch: string) => {
      dispatch(setComparisonBranch(branch))
    },
    [dispatch]
  )

  return {
    ...baseFilters,
    comparisonBranch,
    setComparisonBranch: handleSetComparisonBranch,
  }
}

/**
 * SALES LENS FILTER HOOK
 *
 * For sales-lens pages with temperature and size filters
 * Includes base filters plus sales-specific filters
 */
export const useReduxSalesLensFilter = () => {
  const baseFilters = useReduxBaseFilter()
  const dispatch = useAppDispatch()

  // Select sales-lens specific state
  const temperatureFilter = useAppSelector(selectTemperatureFilter) || 'all'
  const sizeFilter = useAppSelector(selectSizeFilter) || 'all'

  const handleSetTemperatureFilter = useCallback(
    (filter: string) => {
      dispatch(setTemperatureFilter(filter))
    },
    [dispatch]
  )

  const handleSetSizeFilter = useCallback(
    (filter: string) => {
      dispatch(setSizeFilter(filter))
    },
    [dispatch]
  )

  return {
    ...baseFilters,
    temperatureFilter,
    sizeFilter,
    setTemperatureFilter: handleSetTemperatureFilter,
    setSizeFilter: handleSetSizeFilter,
  }
}

/**
 * METRIC FILTER HOOK
 *
 * For lens pages with metric/chart type filters
 * Includes comparison filters plus metric filters
 */
export const useReduxMetricFilter = () => {
  const comparisonFilters = useReduxComparisonFilter()
  const dispatch = useAppDispatch()

  const typeFilter = useAppSelector(selectTypeFilter) || ''
  const chartType = useAppSelector(selectChartType) || ''

  const handleSetTypeFilter = useCallback(
    (filter: string) => {
      dispatch(setTypeFilter(filter))
    },
    [dispatch]
  )

  const handleSetChartType = useCallback(
    (type: string) => {
      dispatch(setChartType(type))
    },
    [dispatch]
  )

  return {
    ...comparisonFilters,
    typeFilter,
    chartType,
    setTypeFilter: handleSetTypeFilter,
    setChartType: handleSetChartType,
  }
}

/**
 * GENERIC FILTER SELECTOR
 *
 * Direct access to filter state without actions
 * Useful when you only need to read values, not update them
 */
export const useFilterState = () => {
  return useAppSelector(selectFilterState)
}

/**
 * Hook to watch for refresh triggers for a specific page
 *
 * @param pageId - The ID of the page to watch
 * @returns boolean - Toggles when refresh is requested
 */
export const usePageRefreshTrigger = (pageId: string) => {
  return useAppSelector(selectPageRefreshTrigger(pageId))
}
